import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
  useRef,
} from "react";
import {
  useExpenses,
  useCreateExpense,
  useUpdateExpense,
  useDeleteExpense,
} from "../hooks/useExpenses";
import { useQueryClient } from "@tanstack/react-query";
import { useSettings, useUpdateSettings } from "../hooks/useSettings";
import {
  useIncomes,
  useCreateIncome,
  useUpdateIncome,
  useDeleteIncome,
} from "../hooks/useIncomes";
import { useNavigate } from "react-router-dom";
import axios from "axios";
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000";
import { getWaveLabel } from "../../translations/translations";
import type { Expense } from "../types/types";
import { AppContext } from "../App";
import ExportMenu from "./DashboardComponents/ExportMenu";
import AiModal from "./DashboardComponents/AiModal";
import OnboardingSteps from "./DashboardComponents/OnboardingSteps";
import { useEmailReport } from "../hooks/useEmailReport";
import { useExportDialog } from "../hooks/useExportDialog";
import PlaidLinkButton from "../components/DashboardComponents/PlaidLinkButton";
import { usePlaidAccounts, useSyncTransactions } from "../hooks/usePlaid";
import ReactGA from "react-ga4";
import Sidebar from "./Sidebar/Sidebar";
import MobileMenu from "./MobileMenu/MobileMenu";
import { useToast } from "./ToastProvider/ToastProvider";
import {
  CardSkeleton,
  TableRowSkeleton,
  StatCardSkeleton,
  IncomeCardSkeleton,
} from "./Skeletons/LoadingSkeletons";
import EmptyState from "./EmptyState/EmptyState";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import {
  useWaveCalculations,
  useFilteredExpenses,
  useHasPaidExpenses,
  handleExpenseFormChange,
} from "../../utils/dashboardHelpers";
import { trackPageView, trackPageDuration } from "../../utils/analytics";
import { EditableWaveAmount } from "./DashboardComponents/EditableWaveAmount";
import AppModal from "./Modal/AppModal";
import { ExpenseForm } from "./DashboardComponents/ExpenseForm";
import QuickAddModal from "./QuickAddModal";
import SelectiveResetModal from "./SelectiveResetModal";
import {
  Zap,
  Trash2,
  Pencil,
  CreditCard,
  MoreVertical,
  Settings,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Repeat,
  Wallet,
  DollarSign,
} from "lucide-react";
import { expenseTemplates } from "../data/expenseTemplates";

function ExpensesList() {
  const context = useContext(AppContext);
  const toast = useToast();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();

  // All useState hooks MUST come first
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [userTier, setUserTier] = useState<"free" | "standard" | "premium">(
    "free",
  );
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [linkBankOpen, setLinkBankOpen] = useState(false);
  const [linkToken, _setLinkToken] = useState<string | null>(null);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const exportMenuRef = useRef<any>(null);
  const [currentUser, setCurrentUser] = useState<{
    name?: string;
    lastName?: string;
    email: string;
    profileImage?: string;
  } | null>(null);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });
  const [selectiveResetOpen, setSelectiveResetOpen] = useState(false);
  const [oneTimeExpensesToDelete, setOneTimeExpensesToDelete] = useState<any[]>(
    [],
  );
  // Removed unused chartDisplayMode state

  // ✅ CRITICAL: Check context early before calling any hooks that depend on it
  if (!context) {
    return (
      <div className="min-h-screen w-full">
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/60"
          style={{ zIndex: 9999 }}
        >
          <div className="w-[60px] h-[60px] border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const token = context.token || null;

  const queryClient = useQueryClient();

  // ✅ Hooks always called unconditionally after early returns
  const { data: plaidAccounts, isLoading: isLoadingAccounts } =
    usePlaidAccounts(token);
  const { mutate: syncTransactions, isPending: isSyncing } =
    useSyncTransactions(token);

  const {
    data: expensesData,
    isLoading: expensesLoading,
    error: expensesError,
    refetch: refetchExpenses,
  } = useExpenses(token || null, { limit: 10000 });
  const {
    data: settingsData,
    isLoading: settingsLoading,
    error: settingsError,
  } = useSettings(token || null);
  const { isLoading: incomesLoading, error: incomesError } = useIncomes(
    token || null,
  );
  const createExpenseMutation = useCreateExpense(token || null);
  const updateExpenseMutation = useUpdateExpense(token || null);
  const deleteExpenseMutation = useDeleteExpense(token || null);
  const createIncomeMutation = useCreateIncome(token || null);
  const updateIncomeMutation = useUpdateIncome(token || null);
  const deleteIncomeMutation = useDeleteIncome(token || null);
  const updateSettingsMutation = useUpdateSettings(token || null);

  const {
    expenses,
    setExpenses,
    newExpense,
    setNewExpense,
    editingId,
    setEditingId,
    //token,
    setToken,
    theme,
    setTheme,
    isFormExpanded,
    setIsFormExpanded,
    openMenuId,
    setOpenMenuId,
    searchQuery,
    setSearchQuery,
    waveFilter,
    setWaveFilter,
    expenseListFrequency,
    setExpenseListFrequency,
    wave1Amount,
    setWave1Amount,
    wave2Amount,
    setWave2Amount,
    wave3Amount,
    setWave3Amount,
    wave4Amount,
    setWave4Amount,
    language,
    setLanguage,
    loading,
    setLoading,
    t,
    isSetupComplete,
    setIsSetupComplete,
    incomeSources,
    setIncomeSources,
  } = context;

  const {
    emailDialogOpen,
    setEmailDialogOpen,
    emailFormat,
    recipientEmail,
    emailError,
    setEmailFormat,
    setRecipientEmail,
    setEmailError,
    sendEmailReport,
    EmailDialog,
  } = useEmailReport({
    expenses,
    waveAmounts: [wave1Amount, wave2Amount, wave3Amount, wave4Amount],
    expenseListFrequency,
    incomeSources,
    token,
    language,
    t,
    toast,
    getWaveLabel,
    API_URL,
    loading,
    setLoading,
  });

  // Export dialog hook
  const { exportDialogOpen, setExportDialogOpen, ExportDialog } =
    useExportDialog({
      language,
      t,
      onExport: async (format) => {
        if (!exportMenuRef.current) return;
        // Get the export functions from ExportMenu
        const menu = exportMenuRef.current as any;
        if (format === "pdf" && menu.exportToPDF) menu.exportToPDF();
        else if (format === "csv" && menu.exportToCSV) menu.exportToCSV();
        else if (format === "excel" && menu.exportToExcel) menu.exportToExcel();
        else if (format === "json" && menu.exportToJSON) menu.exportToJSON();
      },
    });

  // ✅ ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  const hasLinkedBanks = useMemo(() => {
    if (!plaidAccounts || isLoadingAccounts) return false;
    return Boolean(plaidAccounts.accounts?.length);
  }, [plaidAccounts, isLoadingAccounts]);

  // Check if there are any templates available (not already added)
  const hasAvailableTemplates = useMemo(() => {
    const existingNames = new Set(
      (expenses || []).map((exp) => exp.name.toLowerCase().trim()),
    );

    return expenseTemplates.some((template) => {
      const nameEn = template.name.toLowerCase().trim();
      const nameEs = template.nameEs.toLowerCase().trim();
      return !existingNames.has(nameEn) && !existingNames.has(nameEs);
    });
  }, [expenses]);

  // ✅ Keep existing chartOptions unchanged (commented out - unused)
  /* const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: {
            color: theme === "dark" ? "#FFFFFF" : "#000000",
            font: { size: 12, weight: "500" },
          },
        },
      },
      scales: {
        y: {
          ticks: { color: theme === "dark" ? "#8E8E93" : "#3C3C43" },
          grid: {
            color:
              theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
          },
        },
        x: {
          ticks: { color: theme === "dark" ? "#8E8E93" : "#3C3C43" },
          grid: { display: false },
        },
      },
    }),
    [theme]
  ); */

  const {
    waveCount,
    waveTotals,
    waveCounts,
    waveBalances,
    otherExpensesTotal,
    totalWaveExpenses,
    totalWaveAmount, // $ TOTAL of wave expenses (original name)
    //waveBudgets,
  } = useWaveCalculations(
    expenses,
    wave1Amount,
    wave2Amount,
    wave3Amount,
    wave4Amount,
    incomeSources,
    expenseListFrequency,
  );
  const hasPaidExpenses = useHasPaidExpenses(expenses);

  const sortedExpenses = useFilteredExpenses(
    expenses,
    waveFilter,
    searchQuery,
    expenseListFrequency,
  );

  // Removed unused variable - was only used in commented chart code
  /* const primaryBlue = "#007AFF";

  const allIncomeSources = useMemo(() => {
    const mainIncome = {
      id: "main",
      name: t.dashboardPage.mainIncome,
      amount1: wave1Amount,
      amount2: wave2Amount,
      amount3: wave3Amount,
      amount4: wave4Amount,
      frequency: expenseListFrequency,
      color: primaryBlue,
    };

    // Tier-based income limits:
    // Free: 1 income (main only)
    // Standard: 3 incomes (main + 2 sources)
    // Premium: unlimited incomes
    let filteredSources = incomeSources;
    if (userTier === "free") {
      filteredSources = []; // Only main income
    } else if (userTier === "standard") {
      filteredSources = incomeSources.slice(0, 2); // Main + 2 additional
    }
    // Premium gets all sources

    return [mainIncome, ...filteredSources];
  }, [
    incomeSources,
    wave1Amount,
    wave2Amount,
    wave3Amount,
    wave4Amount,
    expenseListFrequency,
    language,
    primaryBlue,
    userTier,
  ]); */

  /* const categoryChartData = useMemo(() => {
    // Calculate total income for each source
    const incomeBySource = allIncomeSources.map(
      (source) =>
        (source.amount1 ?? 0) +
        (source.amount2 ?? 0) +
        (source.amount3 ?? 0) +
        (source.amount4 ?? 0)
    );

    // Calculate total expenses
    const totalExpenses = expenses
      .filter((e) => e.category === "Wave" || e.category === "Paycheck")
      .reduce((sum, e) => sum + e.price, 0);

    // Build labels: all income sources + "Total Expenses"
    const labels = [
      ...allIncomeSources.map((s) => s.name),
      t.dashboardPage.totalExpenses,
    ];

    // Build data: all income amounts + total expenses
    const data = [...incomeBySource, totalExpenses];

    // Build colors: income source colors + orange for expenses
    const backgroundColor = [
      ...allIncomeSources.map((s) => s.color + "80"),
      "rgba(255, 149, 0, 0.5)",
    ];

    const borderColor = [
      ...allIncomeSources.map((s) => s.color),
      "rgba(255, 149, 0, 1)",
    ];

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor,
          borderColor,
          borderWidth: 2,
        },
      ],
    };
  }, [allIncomeSources, expenses, language]); */

  /* const waveBalancesChartData = useMemo(() => {
    return {
      labels: allIncomeSources.map((s) => s.name),
      datasets: [
        {
          label: t.dashboardPage.income,
          data: allIncomeSources.map(
            (s) =>
              (s.amount1 ?? 0) +
              (s.amount2 ?? 0) +
              (s.amount3 ?? 0) +
              (s.amount4 ?? 0)
          ),
          backgroundColor: allIncomeSources.map((s) => s.color + "80"),
          borderColor: allIncomeSources.map((s) => s.color),
          borderWidth: 2,
        },
        {
          label: t.dashboardPage.expenses,
          data: allIncomeSources.map((source) => {
            const sourceExpenses = expenses.filter(
              (e) =>
                (e.category === "Wave" || e.category === "Paycheck") &&
                (source.id === "main"
                  ? !e.incomeSourceId || e.incomeSourceId === ""
                  : e.incomeSourceId === source.id)
            );
            return sourceExpenses.reduce((sum, e) => sum + e.price, 0);
          }),
          backgroundColor: "rgba(255, 149, 0, 0.6)",
          borderColor: "rgba(255, 149, 0, 1)",
          borderWidth: 2,
        },
      ],
    };
  }, [allIncomeSources, expenses, language]); */

  // AI-Powered Savings/Investment Forecast (commented out - unused)
  /* const forecastData = useMemo(() => {
    // Calculate total monthly income
    const totalIncome = allIncomeSources.reduce(
      (sum, source) =>
        sum +
        (source.amount1 ?? 0) +
        (source.amount2 ?? 0) +
        (source.amount3 ?? 0) +
        (source.amount4 ?? 0),
      0
    );

    // Calculate average monthly expenses
    const monthlyExpenses = expenses
      .filter((e) => e.category === "Wave" || e.category === "Paycheck")
      .reduce((sum, e) => sum + e.price, 0);

    // Calculate potential savings
    const potentialSavings = totalIncome - monthlyExpenses;

    // Generate 6-month forecast with AI-like projections
    const months = [
      t.dashboardPage.current,
      t.dashboardPage.month1,
      t.dashboardPage.month2,
      t.dashboardPage.month3,
      t.dashboardPage.month4,
      t.dashboardPage.month5,
      t.dashboardPage.month6,
    ];

    // Conservative savings forecast (50% of potential)
    const conservativeForecast = months.map((_, idx) => {
      if (idx === 0) return 0;
      return (potentialSavings * 0.5 * idx).toFixed(2);
    });

    // Moderate savings forecast (70% of potential)
    const moderateForecast = months.map((_, idx) => {
      if (idx === 0) return 0;
      return (potentialSavings * 0.7 * idx).toFixed(2);
    });

    // Optimistic savings forecast (90% of potential with 5% compound interest)
    const optimisticForecast = months.map((_, idx) => {
      if (idx === 0) return 0;
      const monthlyRate = 0.05 / 12; // 5% annual rate
      let total = 0;
      for (let i = 1; i <= idx; i++) {
        total = (total + potentialSavings * 0.9) * (1 + monthlyRate);
      }
      return total.toFixed(2);
    });

    return {
      labels: months,
      datasets: [
        {
          label: t.dashboardPage.conservativeForecast,
          data: conservativeForecast,
          borderColor: "rgba(255, 149, 0, 1)",
          backgroundColor: "rgba(255, 149, 0, 0.1)",
          borderWidth: 2,
          tension: 0.4,
          fill: true,
        },
        {
          label: t.dashboardPage.moderateForecast,
          data: moderateForecast,
          borderColor: "rgba(52, 199, 89, 1)",
          backgroundColor: "rgba(52, 199, 89, 0.1)",
          borderWidth: 2,
          tension: 0.4,
          fill: true,
        },
        {
          label: t.dashboardPage.optimisticForecast,
          data: optimisticForecast,
          borderColor: "rgba(0, 122, 255, 1)",
          backgroundColor: "rgba(0, 122, 255, 0.1)",
          borderWidth: 2,
          tension: 0.4,
          fill: true,
        },
      ],
    };
  }, [allIncomeSources, expenses, language]); */

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  }, [setTheme]);

  const addExpense = async (expenseData: {
    name: string;
    price: number;
    dueDate?: string;
    wave?: number;
    category: "Wave" | "Paycheck" | "Other";
    paymentUrl?: string;
    paymentType?: "url" | "app" | "autopay";
    isPaid?: boolean;
    frequency?: "weekly" | "biweekly" | "monthly";
    incomeSourceId?: string;
    isTrackingOnly?: boolean;
  }) => {
    // ✅ Validation - dueDate only required if not tracking-only
    if (
      !expenseData.name.trim() ||
      (!expenseData.isTrackingOnly && !expenseData.dueDate) ||
      expenseData.price <= 0 ||
      !expenseData.category
    ) {
      toast.error(t.errorMessages.invalidFields);
      return;
    }
    if (expenseData.category === "Wave" && expenseData.wave === undefined) {
      toast.error(t.errorMessages.invalidWave);
      return;
    }
    if (expenseData.category === "Paycheck") {
      if (!expenseData.frequency) {
        toast.error(t.errorMessages.invalidPaycheckFrequency);
        return;
      }
      const maxWaves =
        expenseData.frequency === "weekly"
          ? 4
          : expenseData.frequency === "biweekly"
            ? 2
            : 1;
      if (
        expenseData.wave === undefined ||
        expenseData.wave < 1 ||
        expenseData.wave > maxWaves
      ) {
        toast.error(
          t.errorMessages.invalidPaycheckWave(maxWaves, expenseData.frequency),
        );
        return;
      }
    }

    const data = {
      ...expenseData,
      paymentUrl:
        expenseData.paymentType === "autopay"
          ? undefined
          : expenseData.paymentUrl,
      wave:
        expenseData.category === "Paycheck" || expenseData.category === "Wave"
          ? expenseData.wave
          : undefined,
      category:
        expenseData.category === "Paycheck" ? "Wave" : expenseData.category,
      isPaid: expenseData.isPaid ?? false,
      incomeSourceId: expenseData.incomeSourceId,
    };

    if (
      data.paymentType !== "autopay" &&
      data.paymentUrl &&
      !/^(https?:\/\/|[\w-]+:)/.test(data.paymentUrl)
    ) {
      toast.error(t.errorMessages.invalidPaymentUrl);
      return;
    }

    // ✅ Use React Query mutations
    if (editingId) {
      updateExpenseMutation.mutate(
        { id: editingId, updates: data },
        {
          onSuccess: () => {
            toast.success(t.successMessages.expenseUpdated);
            setEditingId(null);
            setNewExpense({
              name: "",
              price: 0,
              dueDate: "",
              wave: 1,
              category: "Wave",
              paymentUrl: "",
              paymentType: "url",
              frequency: "biweekly",
              incomeSourceId: "",
              isTrackingOnly: false,
            });
            setIsFormExpanded(false);
          },
          onError: (error: any) => {
            toast.error(
              error.response?.data?.error || t.errorMessages.saveExpenseFailed,
            );
          },
        },
      );
    } else {
      createExpenseMutation.mutate(data as Expense, {
        onSuccess: () => {
          toast.success(t.successMessages.expenseAdded);
          setNewExpense({
            name: "",
            price: 0,
            dueDate: "",
            wave: 1,
            category: "Wave",
            paymentUrl: "",
            paymentType: "url",
            frequency: "biweekly",
            incomeSourceId: "",
            isTrackingOnly: false,
          });
          setIsFormExpanded(false);
          ReactGA.event({
            category: "Expense",
            action: "Add Expense",
            label: "ExpensesList",
            value: expenseData.price,
          });
        },
        onError: (error: any) => {
          toast.error(
            error.response?.data?.error || t.errorMessages.saveExpenseFailed,
          );
        },
      });
    }
  };

  const togglePaid = useCallback(
    (id: string, isPaid: boolean) => {
      updateExpenseMutation.mutate(
        { id, updates: { isPaid: !isPaid } },
        {
          onSuccess: () => {
            toast.success(`Expense marked as ${!isPaid ? t.paid : t.unpaid}!`);
          },
          onError: (error: any) => {
            toast.error(
              error.response?.data?.error ||
                t.errorMessages.updateExpenseFailed,
            );
          },
        },
      );
    },
    [updateExpenseMutation, t, toast],
  );

  const payExpense = useCallback(
    (
      id: string,
      paymentUrl?: string,
      paymentType?: "url" | "app" | "autopay",
    ) => {
      if (!paymentUrl || paymentType === "autopay") {
        toast.error(t.errorMessages.noPaymentUrl);
        return;
      }

      window.open(paymentUrl, paymentType === "url" ? "_blank" : "_self");

      const timer = setTimeout(() => {
        setConfirmModal({
          open: true,
          title: t.dashboardPage.paymentConfirmation,
          message: t.dashboardPage.didPaymentSucceed,
          onConfirm: () => {
            updateExpenseMutation.mutate(
              { id, updates: { isPaid: true } },
              {
                onSuccess: () =>
                  toast.success(t.successMessages.paymentConfirmed),
              },
            );
            setConfirmModal((prev) => ({ ...prev, open: false }));
          },
        });
      }, 30_000);

      return () => clearTimeout(timer);
    },
    [language, updateExpenseMutation, t, toast],
  );

  const deleteExpense = useCallback(
    (id: string) => {
      setConfirmModal({
        open: true,
        title: t.dashboardPage.deleteExpense,
        message: t.dashboardPage.deleteExpenseConfirmation,
        onConfirm: () => {
          deleteExpenseMutation.mutate(id, {
            onSuccess: () => {
              toast.success(t.successMessages.expenseDeleted);
            },
            onError: (error: any) => {
              toast.error(
                error.response?.data?.error ||
                  t.errorMessages.deleteExpenseFailed,
              );
            },
          });
          setConfirmModal((prev) => ({ ...prev, open: false }));
        },
      });
    },
    [deleteExpenseMutation, t, toast, language],
  );

  const editExpense = useCallback(
    (expense: Expense) => {
      setEditingId(expense._id);
      const frequency =
        expense.category === "Paycheck" && expense.wave && expense.wave > 2
          ? "weekly"
          : expense.category === "Paycheck" && expense.wave && expense.wave <= 2
            ? "biweekly"
            : expense.category === "Paycheck"
              ? "monthly"
              : "biweekly";
      setNewExpense({
        name: expense.name,
        price: expense.price,
        dueDate: expense.dueDate || "",
        wave: expense.wave,
        category: expense.category,
        paymentUrl: expense.paymentUrl || "",
        paymentType: expense.paymentType || "url",
        frequency,
        incomeSourceId: expense.incomeSourceId || "",
        isTrackingOnly: expense.isTrackingOnly || false,
      });
      setIsFormExpanded(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [context],
  );

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (!token) return;
      try {
        const { data } = await axios.get(`${API_URL}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCurrentUser(data);
      } catch (error) {
        console.error("Failed to fetch current user:", error);
      }
    };
    fetchCurrentUser();
  }, [token]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        openMenuId &&
        !(e.target as Element).closest(".expense-menu-container")
      ) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [openMenuId]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      if (!localStorage.getItem("theme")) {
        setTheme(mediaQuery.matches ? "dark" : "light");
      }
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // ✅ Sync with context when data changes
  useEffect(() => {
    if (expensesData?.expenses) {
      const transformedExpenses = expensesData.expenses.map((exp: any) => ({
        ...exp,
        category: exp.category === "Wave" ? "Paycheck" : exp.category,
      }));
      setExpenses(transformedExpenses);
    }
  }, [expensesData]); // setExpenses is stable, no need to include

  useEffect(() => {
    if (expensesError) {
      toast.error(t.errorMessages.fetchExpensesFailed);
    }
  }, [expensesError, t]); // Simplified from t.errorMessages.fetchExpensesFailed

  useEffect(() => {
    const isAnyMutationPending =
      createExpenseMutation.isPending ||
      updateExpenseMutation.isPending ||
      deleteExpenseMutation.isPending ||
      createIncomeMutation.isPending ||
      updateIncomeMutation.isPending ||
      deleteIncomeMutation.isPending;

    setLoading(isAnyMutationPending);
  }, [
    createExpenseMutation.isPending,
    updateExpenseMutation.isPending,
    deleteExpenseMutation.isPending,
    createIncomeMutation.isPending,
    updateIncomeMutation.isPending,
    deleteIncomeMutation.isPending,
    // setLoading is stable, no need to include
  ]);

  useEffect(() => {
    if (settingsData) {
      setWave1Amount(settingsData.wave1Amount || 0);
      setWave2Amount(settingsData.wave2Amount || 0);
      setExpenseListFrequency(settingsData.frequency);
      setLanguage(settingsData.language);
      setUserTier(settingsData.subscriptionTier || "free");

      // ✅ Income sources with colors
      const sources = (settingsData.incomeSources || []).map(
        (src: any, idx: number) => {
          const defaultColors = [
            "#007AFF",
            "#34C759",
            "#AF52DE",
            "#FF9500",
            "#FF3B30",
            "#5856D6",
          ];
          return {
            id: src.id || src._id,
            name: src.name,
            amount1: src.amount1 ?? 0,
            amount2: src.amount2 ?? 0,
            amount3: src.amount3 ?? 0,
            amount4: src.amount4 ?? 0,
            frequency: src.frequency,
            color: src.color || defaultColors[idx % defaultColors.length],
          };
        },
      );
      setIncomeSources(sources);

      // ✅ CRITICAL FIX: Proper onboarding logic
      if (settingsData.isSetupComplete === false && !showOnboarding) {
        setIsSetupComplete(false);
        setShowOnboarding(true);
      } else if (settingsData.isSetupComplete === true) {
        setIsSetupComplete(true);
        setShowOnboarding(false);
      }
    }
  }, [settingsData]); // ✅ ONLY settingsData - no setters!

  // ✅ Handle settings errors
  useEffect(() => {
    if (settingsError) {
      toast.error(t.errorMessages.fetchSettingsFailed);
    }
  }, [settingsError, t]); // Simplified from t.errorMessages.fetchSettingsFailed

  // ✅ Handle incomes errors
  useEffect(() => {
    if (incomesError) {
      toast.error(t.dashboardPage.failedToFetchIncomes);
    }
  }, [incomesError, language]);

  useEffect(() => {
    const startTime = Date.now();
    trackPageView("/dashboard");

    return () => {
      trackPageDuration("/dashboard", startTime);
    };
  }, []);

  const cancelEdit = () => {
    setEditingId(null);
    setNewExpense({
      name: "",
      price: 0,
      dueDate: "",
      wave: 1,
      category: "Wave",
      paymentUrl: "",
      paymentType: "url",
      frequency: "biweekly",
      incomeSourceId: "",
      isTrackingOnly: false,
    });
    setIsFormExpanded(false);
  };

  const resetAllPaid = async () => {
    // Check for recurring expenses that are paid
    const paidRecurringExpenses = expenses.filter(
      (e) => e.isPaid && e.isRecurring,
    );

    // Check for non-recurring paid expenses (one-time expenses)
    const paidOneTimeExpenses = expenses.filter(
      (e) => e.isPaid && !e.isRecurring && e.category !== "Other",
    );

    if (
      paidRecurringExpenses.length === 0 &&
      paidOneTimeExpenses.length === 0
    ) {
      toast.warning(t.noExpensesToReset || "No paid expenses to reset");
      return;
    }

    // Confirm reset of recurring expenses
    setConfirmModal({
      open: true,
      title: t.dashboardPage.resetAllExpenses,
      message:
        t.resetRecurringExpensesConfirm?.(paidRecurringExpenses.length) ||
        `Reset ${paidRecurringExpenses.length} recurring expenses for the new cycle?`,
      onConfirm: async () => {
        setConfirmModal((prev) => ({ ...prev, open: false }));
        setLoading(true);
        try {
          // Step 1: Reset recurring expenses via API
          if (paidRecurringExpenses.length > 0) {
            const response = await axios.put(
              `${API_URL}/expenses/reset-cycle`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              },
            );

            // Optimistic local update so the UI reflects unchecked recurring
            // expenses immediately, regardless of cache timing.
            setExpenses((prev) =>
              prev.map((e) =>
                e.isRecurring ? { ...e, isPaid: false } : e,
              ),
            );
            queryClient.setQueryData(["expenses", token], (old: any) => {
              if (!old?.expenses) return old;
              return {
                ...old,
                expenses: old.expenses.map((e: any) =>
                  e.isRecurring ? { ...e, isPaid: false } : e,
                ),
              };
            });

            toast.success(
              t.recurringExpensesResetSuccess?.(response.data.resetCount) ||
                `${response.data.resetCount} recurring expenses reset successfully`,
            );
          }

          // Step 2: Invalidate + refetch to get authoritative server data
          await queryClient.invalidateQueries({
            queryKey: ["expenses", token],
          });
          await refetchExpenses();

          // Step 3: Show modal for one-time expenses deletion
          if (paidOneTimeExpenses.length > 0) {
            setOneTimeExpensesToDelete(paidOneTimeExpenses);
            setSelectiveResetOpen(true);
          }
        } catch (error: any) {
          console.error("Reset cycle error:", error);
          toast.error(
            error.response?.data?.error ||
              t.errorMessages?.resetExpensesFailed ||
              "Failed to reset expenses",
          );
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Handle deletion of selected one-time expenses
  const handleDeleteOneTimeExpenses = async (expenseIds: string[]) => {
    setLoading(true);
    try {
      // Use direct axios calls for bulk deletion to avoid React Query mutation state conflicts
      await Promise.all(
        expenseIds.map((id) =>
          axios.delete(`${API_URL}/expenses/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ),
      );
      toast.success(
        t.dashboardPage?.expensesDeletedSuccess?.(expenseIds.length) ||
          `${expenseIds.length} one-time expenses deleted successfully`,
      );
      setSelectiveResetOpen(false);
      await refetchExpenses();
    } catch (error: any) {
      toast.error(
        error.response?.data?.error ||
          t.errorMessages?.deleteExpensesFailed ||
          "Failed to delete expenses",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      handleExpenseFormChange(e, setNewExpense);
    },
    [setNewExpense],
  );

  // ✅ Handle loading state in render instead of early return
  if (isSetupComplete === null) {
    return (
      <div
        className="fixed inset-0 flex items-center justify-center bg-black/60"
        style={{ zIndex: 1300 }}
      >
        <div className="w-[60px] h-[60px] border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* Glassmorphic Sidebar - Desktop Only */}
      <Sidebar
        theme={theme}
        toggleTheme={toggleTheme}
        language={language}
        setLanguage={setLanguage}
        t={t}
        onLogout={() => {
          setToken(null);
          localStorage.removeItem("token");
          localStorage.removeItem("biometricCredentialId");
          navigate("/auth");
        }}
        currentUser={currentUser}
        hasPaidExpenses={hasPaidExpenses}
        onResetAllPaid={resetAllPaid}
        onExportClick={() => setExportDialogOpen(true)}
        onEmailReport={() => setEmailDialogOpen(true)}
        onAiAdvisor={() => setAiModalOpen(true)}
        userTier={userTier}
        hasLinkedBanks={hasLinkedBanks}
        plaidAccountsCount={plaidAccounts?.accounts?.length ?? 0}
        plaidAccounts={plaidAccounts}
        onBankSync={() => {
          syncTransactions(undefined, {
            onSuccess: () => {
              toast.success(t.dashboardPage.transactionsSyncedSuccess);
              refetchExpenses();
            },
            onError: (error: any) => {
              toast.error(
                error.response?.data?.error ||
                  t.dashboardPage.failedToSyncTransactions,
              );
            },
          });
        }}
        isSyncing={isSyncing}
        onLinkBank={() => setLinkBankOpen(true)}
        onUpgradeToPremium={() => navigate("/subscription")}
      />

      {/* Mobile Menu - Only visible on mobile */}
      <MobileMenu
        theme={theme}
        toggleTheme={toggleTheme}
        language={language}
        t={t}
        hasPaidExpenses={hasPaidExpenses}
        userTier={userTier}
        navigate={navigate}
        resetAllPaid={resetAllPaid}
        setEmailDialogOpen={setEmailDialogOpen}
        setAiModalOpen={setAiModalOpen}
        setToken={setToken}
        token={token}
        linkToken={linkToken}
        toast={toast}
        refetchExpenses={refetchExpenses}
        currentUser={currentUser}
        ExportMenuComponent={
          <ExportMenu
            expenses={expenses}
            waveAmounts={[wave1Amount, wave2Amount, wave3Amount, wave4Amount]}
            incomeSources={incomeSources}
            expenseListFrequency={expenseListFrequency}
            waveCount={waveCount}
            waveTotals={waveTotals}
            waveCounts={waveCounts}
            waveBalances={waveBalances}
            t={t}
            language={language}
            toast={toast}
            getWaveLabel={getWaveLabel}
          />
        }
      />
      <div className="app-container with-sidebar overflow-x-hidden">
        {!isOnline && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              background: "#ff9500",
              color: "white",
              padding: "0.75rem",
              textAlign: "center",
              fontWeight: "600",
              zIndex: 9999,
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            }}
          >
            📡 {t.dashboardPage.offlineMessage}
          </div>
        )}
        {loading && (
          <div
            className="fixed inset-0 flex items-center justify-center bg-black/60"
            style={{ zIndex: 1300, backdropFilter: "blur(4px)" }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "1rem",
              }}
            >
              <div className="w-[40px] h-[40px] border-2 border-white/20 border-t-white rounded-full animate-spin" />
              <p
                style={{
                  fontSize: "0.9rem",
                  opacity: 0.9,
                  textAlign: "center",
                }}
              >
                {t.dashboardPage.savingChanges}
              </p>
            </div>
          </div>
        )}

        <div className="w-full max-w-[900px] mx-auto px-4 py-6 md:px-6 md:py-10">
          <div className="bg-card-light dark:bg-card border border-card-border-light dark:border-card-border backdrop-blur-glass rounded-card p-4 md:p-6 mb-4 md:mb-5 shadow-glass-light dark:shadow-glass w-full">
            {hasLinkedBanks &&
              plaidAccounts?.accounts &&
              Array.isArray(plaidAccounts.accounts) && (
                <>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-chip text-xs font-semibold bg-green/10 text-green border border-green/20 mb-2">
                    <Wallet size={14} />
                    {`${plaidAccounts.accounts.length ?? 0} Bank(s) Linked`}
                  </span>
                </>
              )}
            <div
              style={{
                display: "flex",
                gap: "1rem",
                marginBottom: "1rem",
                flexWrap: "wrap",
              }}
            >
              {hasAvailableTemplates && (
                <button
                  onClick={() => setQuickAddOpen(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-btn px-6 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 shadow-primary-glow hover:shadow-[0_6px_20px_rgba(0,122,255,0.4)]"
                  style={{
                    background:
                      "linear-gradient(135deg, #007AFF 0%, #0051D5 100%)",
                    flex: "1 1 auto",
                    minWidth: "180px",
                  }}
                >
                  <Zap size={18} />
                  {t.quickAdd || "Quick Add"}
                </button>
              )}
              <button
                className="inline-flex items-center justify-center gap-2 rounded-btn px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 shadow-primary-glow"
                onClick={() => setIsFormExpanded(!isFormExpanded)}
                style={{
                  flex: "1 1 auto",
                  minWidth: "180px",
                  background:
                    "linear-gradient(135deg, #007AFF 0%, #0051D5 100%)",
                }}
              >
                <span
                  className={`text-xl font-medium text-white transition-transform duration-300 ${isFormExpanded ? "rotate-45" : ""}`}
                >
                  +
                </span>
                {editingId
                  ? t.editExpense
                  : isFormExpanded
                    ? t.hideForm
                    : t.addNewExpense}
              </button>
            </div>
            <div className={`form-content ${isFormExpanded ? "expanded" : ""}`}>
              <ExpenseForm
                newExpense={newExpense}
                handleChange={handleChange}
                addExpense={addExpense}
                cancelEdit={cancelEdit}
                editingId={editingId}
                loading={loading}
                language={language}
                t={t}
                expenseListFrequency={expenseListFrequency}
                incomeSources={incomeSources}
                wave1Amount={wave1Amount}
                wave2Amount={wave2Amount}
                wave3Amount={wave3Amount}
                wave4Amount={wave4Amount}
                theme={theme}
              />
              {/* <div className="card" style={{ marginTop: "2rem" }}> */}
              <div
                style={{
                  background: `linear-gradient(135deg, var(--primary-blue), var(--primary-blue-dark))`,
                  padding: "1.5rem",
                  borderRadius: "12px",
                  color: "white",
                  marginTop: "2rem",
                }}
              >
                <h3
                  style={{
                    margin: "0 0 1.5rem 0",
                    fontSize: "1.1em",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <DollarSign size={20} />
                  {t.dashboardPage.incomeSources}
                </h3>

                {/* Main Income - Always shown first */}
                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.15)",
                    padding: "1rem",
                    borderRadius: "8px",
                    marginBottom: "0.75rem",
                    border: "2px solid rgba(255, 255, 255, 0.3)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          marginBottom: "0.5rem",
                        }}
                      >
                        <span style={{ fontWeight: "600", fontSize: "1.05em" }}>
                          {t.dashboardPage.mainWaveIncome}
                        </span>
                      </div>

                      {/* Wave 1 */}
                      <div
                        style={{
                          opacity: 0.95,
                          fontSize: "0.95em",
                          marginBottom: "0.5rem",
                        }}
                      >
                        <strong>
                          {getWaveLabel(1, expenseListFrequency, t)}:
                        </strong>
                        <EditableWaveAmount
                          waveNumber={1}
                          amount={wave1Amount}
                          onSave={async (newAmount) => {
                            await updateSettingsMutation.mutateAsync({
                              wave1Amount: newAmount,
                            });
                            setWave1Amount(newAmount);
                            toast.success(
                              t.dashboardPage.waveAmountUpdated ||
                                "Wave amount updated successfully",
                            );
                          }}
                          frequency={expenseListFrequency}
                          language={language}
                        />
                      </div>

                      {/* Wave 2 (only for biweekly) */}
                      {expenseListFrequency === "biweekly" && (
                        <div style={{ opacity: 0.95, fontSize: "0.95em" }}>
                          <strong>
                            {getWaveLabel(2, expenseListFrequency, t)}:
                          </strong>
                          <EditableWaveAmount
                            waveNumber={2}
                            amount={wave2Amount}
                            onSave={async (newAmount) => {
                              await updateSettingsMutation.mutateAsync({
                                wave2Amount: newAmount,
                              });
                              setWave2Amount(newAmount);
                              toast.success(
                                t.dashboardPage.waveAmountUpdated ||
                                  "Wave amount updated successfully",
                              );
                            }}
                            frequency={expenseListFrequency}
                            language={language}
                          />
                        </div>
                      )}
                    </div>

                    <div style={{ textAlign: "right" }}>
                      <div
                        style={{
                          fontSize: "0.8em",
                          opacity: 0.85,
                          marginBottom: "0.25rem",
                        }}
                      >
                        {t.dashboardPage.monthlyEstimate}
                      </div>
                      <div style={{ fontWeight: "bold", fontSize: "1.1em" }}>
                        $
                        {(
                          (wave1Amount + wave2Amount) *
                          (expenseListFrequency === "biweekly" ? 1.085 : 1)
                        ).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Income Sources */}
                {incomesLoading ? (
                  // ✅ Show skeleton income cards while loading
                  <div style={{ marginTop: "1rem" }}>
                    <h3
                      style={{
                        color: "var(--text-color)",
                        marginBottom: "1rem",
                      }}
                    >
                      {t.dashboardPage.yourIncomeSources}
                    </h3>
                    <IncomeCardSkeleton />
                    <IncomeCardSkeleton />
                    <IncomeCardSkeleton />
                  </div>
                ) : (
                  incomeSources.length > 0 && (
                    <>
                      {incomeSources.map((income) => {
                        const waves = [
                          { label: "Wave 1", amount: income.amount1 },
                          { label: "Wave 2", amount: income.amount2 },
                          { label: "Wave 3", amount: income.amount3 },
                          { label: "Wave 4", amount: income.amount4 },
                        ].filter(
                          (wave) => wave.amount != null && wave.amount > 0,
                        );

                        const monthlyTotal =
                          (income.amount1 ?? 0) +
                          (income.amount2 ?? 0) +
                          (income.amount3 ?? 0) +
                          (income.amount4 ?? 0);

                        return (
                          <div
                            key={income.id}
                            style={{
                              background: "rgba(255, 255, 255, 0.1)",
                              padding: "1rem",
                              borderRadius: "8px",
                              marginBottom: "0.75rem",
                              borderLeft: `4px solid ${income.color}`,
                            }}
                          >
                            <div
                              style={{
                                fontWeight: "600",
                                fontSize: "1.05em",
                                marginBottom: "0.5rem",
                              }}
                            >
                              {income.name}
                            </div>

                            {waves.map((wave, idx) => (
                              <div
                                key={idx}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  padding: "0.5rem 0",
                                  borderBottom:
                                    idx < waves.length - 1
                                      ? "1px solid rgba(255,255,255,0.1)"
                                      : "none",
                                }}
                              >
                                <div style={{ opacity: 0.85 }}>
                                  {wave.label}:
                                </div>
                                <div style={{ fontWeight: "500" }}>
                                  ${(wave.amount ?? 0).toFixed(2)}
                                </div>
                              </div>
                            ))}

                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginTop: "0.75rem",
                                paddingTop: "0.75rem",
                                borderTop: "1px solid rgba(255,255,255,0.2)",
                              }}
                            >
                              <div style={{ fontWeight: "600" }}>
                                {t.dashboardPage.monthlyTotal}:
                              </div>
                              <div
                                style={{
                                  fontWeight: "bold",
                                  fontSize: "1.1em",
                                }}
                              >
                                ${monthlyTotal.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )
                )}

                {/* Total Monthly Income Summary */}
                <div
                  style={{
                    borderTop: "2px solid rgba(255, 255, 255, 0.3)",
                    marginTop: "1rem",
                    paddingTop: "1rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "1.3em",
                      fontWeight: "bold",
                    }}
                  >
                    <span>{t.dashboardPage.totalMonthlyIncome}</span>
                    <span>
                      $
                      {(
                        wave1Amount +
                        wave2Amount +
                        wave3Amount +
                        wave4Amount +
                        incomeSources.reduce(
                          (sum, inc) =>
                            sum +
                            (inc.amount1 ?? 0) +
                            (inc.amount2 ?? 0) +
                            (inc.amount3 ?? 0) +
                            (inc.amount4 ?? 0),
                          0,
                        )
                      ).toFixed(2)}
                    </span>
                  </div>

                  {/* Total Expenses */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "1em",
                      marginTop: "0.5rem",
                      opacity: 0.9,
                    }}
                  >
                    <span>{t.dashboardPage.totalExpensesLabel}</span>
                    <span>
                      $
                      {expenses
                        .filter(
                          (e) =>
                            e.category === "Wave" || e.category === "Paycheck",
                        )
                        .reduce((sum, e) => sum + e.price, 0)
                        .toFixed(2)}
                    </span>
                  </div>

                  {/* Net Balance */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "1.1em",
                      marginTop: "0.5rem",
                      fontWeight: "600",
                      color:
                        wave1Amount +
                          wave2Amount +
                          wave3Amount +
                          wave4Amount +
                          incomeSources.reduce(
                            (sum, inc) =>
                              sum +
                              (inc.amount1 ?? 0) +
                              (inc.amount2 ?? 0) +
                              (inc.amount3 ?? 0) +
                              (inc.amount4 ?? 0),
                            0,
                          ) -
                          expenses
                            .filter(
                              (e) =>
                                e.category === "Wave" ||
                                e.category === "Paycheck",
                            )
                            .reduce((sum, e) => sum + e.price, 0) >=
                        0
                          ? "#34C759"
                          : "#FF3B30",
                    }}
                  >
                    <span>{t.dashboardPage.netBalance}</span>
                    <span>
                      $
                      {(
                        wave1Amount +
                        wave2Amount +
                        wave3Amount +
                        wave4Amount +
                        incomeSources.reduce(
                          (sum, inc) =>
                            sum +
                            (inc.amount1 ?? 0) +
                            (inc.amount2 ?? 0) +
                            (inc.amount3 ?? 0) +
                            (inc.amount4 ?? 0),
                          0,
                        ) -
                        expenses
                          .filter(
                            (e) =>
                              e.category === "Wave" ||
                              e.category === "Paycheck",
                          )
                          .reduce((sum, e) => sum + e.price, 0)
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Manage Button - Links to Settings */}
                <button
                  onClick={() => navigate("/settings")}
                  style={{
                    width: "100%",
                    marginTop: "1.5rem",
                    padding: "0.75rem",
                    borderRadius: "8px",
                    border: "2px solid rgba(255, 255, 255, 0.5)",
                    background: "rgba(255, 255, 255, 0.1)",
                    color: "white",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background =
                      "rgba(255, 255, 255, 0.2)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background =
                      "rgba(255, 255, 255, 0.1)";
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <Settings size={16} />
                    {t.dashboardPage.manageIncomeSources}
                  </span>
                </button>
              </div>
              {/* </div> */}
            </div>
          </div>
          <div className="bg-card-light dark:bg-card border border-card-border-light dark:border-card-border backdrop-blur-glass rounded-card p-4 md:p-6 mb-4 md:mb-5 shadow-glass-light dark:shadow-glass w-full">
            <div className="flex justify-between items-stretch flex-wrap gap-3 mb-4">
              <h2 className="flex items-center text-lg font-semibold text-text-light dark:text-text m-0 flex-[1_1_100%] sm:flex-[0_1_auto] mb-2 sm:mb-0">
                {t.expenseList}
              </h2>
              <div className="flex items-center flex-1 min-w-0 sm:flex-[0_1_200px]">
                <div className="relative w-full">
                  <Search
                    className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                    size={16}
                    style={{ color: theme === "light" ? "#3C3C43" : "#EBEBF5" }}
                  />
                  <input
                    type="text"
                    className="w-full h-[38px] pl-9 pr-3 text-sm rounded-chip border border-card-border-light dark:border-card-border bg-black/[0.04] dark:bg-white/[0.06] text-text-light dark:text-text outline-none transition-all focus:border-primary placeholder:text-muted-light dark:placeholder:text-muted box-border"
                    placeholder={t.searchExpenses}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-center flex-1 min-w-0 sm:flex-[0_1_140px]">
                <div className="relative w-full">
                  <SlidersHorizontal
                    className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                    size={16}
                    style={{ color: theme === "light" ? "#3C3C43" : "#EBEBF5" }}
                  />
                  <select
                    className="w-full h-[38px] pl-9 pr-3 text-sm rounded-chip border border-card-border-light dark:border-card-border bg-black/[0.04] dark:bg-white/[0.06] text-text-light dark:text-text outline-none appearance-none cursor-pointer box-border"
                    style={{ colorScheme: theme === "dark" ? "dark" : "light" }}
                    value={waveFilter}
                    onChange={(e) =>
                      setWaveFilter(
                        e.target.value as
                          | "All"
                          | "Week 1"
                          | "Week 2"
                          | "Week 3"
                          | "Week 4"
                          | "Wave 1"
                          | "Wave 2"
                          | "Credit Card",
                      )
                    }
                  >
                    <option value="All">{t.all}</option>
                    {Array.from({ length: waveCount }, (_, i) => (
                      <option
                        key={i + 1}
                        value={
                          expenseListFrequency === "weekly"
                            ? `Week ${i + 1}`
                            : `Wave ${i + 1}`
                        }
                      >
                        {getWaveLabel(i + 1, expenseListFrequency, t)}
                      </option>
                    ))}
                    {/* Only show Credit Card filter if Plaid accounts with credit cards exist */}
                    {plaidAccounts?.accounts?.some((bank) =>
                      bank.accounts.some((acc) => acc.type === "credit"),
                    ) && (
                      <option value="Credit Card">
                        {language === "en"
                          ? "Credit Card Only"
                          : "Solo Tarjeta de Crédito"}
                      </option>
                    )}
                  </select>
                </div>
              </div>
            </div>
            <div className="grid gap-3 w-full md:hidden" style={{ gridTemplateColumns: "minmax(0, 1fr)" }}>
              {expensesLoading ? (
                // ✅ Show skeletons based on actual expense count
                <>
                  {Array.from({
                    length: Math.max(sortedExpenses.length || 4, 1),
                  }).map((_, i) => (
                    <CardSkeleton key={i} />
                  ))}
                </>
              ) : sortedExpenses.length === 0 ? (
                // ✅ Show empty state when no expenses
                <EmptyState
                  icon=<CreditCard size={24} />
                  title={t.dashboardPage.noExpensesYet}
                  description={t.dashboardPage.addFirstExpenseDescription}
                  actionLabel={t.dashboardPage.addExpense}
                  onAction={() => setIsFormExpanded(true)}
                />
              ) : (
                sortedExpenses.map((exp) => {
                  const incomeSource = incomeSources.find(
                    (s) => s.id === exp.incomeSourceId,
                  );
                  const borderColor =
                    incomeSource?.color || "var(--primary-blue)";
                  return (
                    <div
                      key={exp._id}
                      className="expense-row bg-card-light dark:bg-card border border-card-border-light dark:border-card-border rounded-chip pl-2 pr-1.5 py-2.5 w-full max-w-full min-w-0 relative z-[1] transition-all overflow-visible box-border"
                      style={{
                        borderLeft: `4px solid ${borderColor}`,
                        backgroundColor: incomeSource
                          ? `${incomeSource.color}05`
                          : "transparent",
                        display: "grid",
                        gridTemplateColumns:
                          "auto minmax(0, 1fr) auto auto auto auto",
                        alignItems: "center",
                        columnGap: "0.25rem",
                      }}
                    >
                      {/* ... rest of expense card code stays the same ... */}
                      {incomeSources.length > 1 && incomeSource && (
                        <div
                          style={{
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                            background: incomeSource.color,
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "6px",
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            zIndex: 1,
                          }}
                        >
                          {incomeSource.name}
                        </div>
                      )}
                      <input
                        type="checkbox"
                        className="w-[1.1rem] h-[1.1rem] cursor-pointer flex-shrink-0 rounded-[6px] accent-primary"
                        checked={exp.isPaid}
                        onChange={() => togglePaid(exp._id, exp.isPaid)}
                      />
                      <span className="text-sm font-medium text-text-light dark:text-text min-w-0 w-full flex items-center gap-1.5 overflow-hidden">
                        <span className="truncate min-w-0">{exp.name}</span>
                        {exp.category === "BNPL" && (
                          <span className="flex-shrink-0 text-[0.65rem] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "rgba(123,97,255,0.15)", color: "#7B61FF" }}>BNPL</span>
                        )}
                      </span>
                      <span className="text-sm font-semibold text-text-light dark:text-text flex-shrink-0 whitespace-nowrap">
                        ${exp.price.toFixed(2)}
                      </span>
                      <span className="day-badge text-[0.7rem] font-semibold text-muted-light dark:text-muted flex-shrink-0 px-1.5 py-0.5 bg-black/[0.06] dark:bg-white/[0.08] rounded-[6px] min-w-[1.5rem] text-center whitespace-nowrap">
                        {exp.isTrackingOnly ? (
                          <span
                            className="track-only-badge"
                            style={{
                              fontSize: "0.7rem",
                              color: "var(--primary-blue)",
                            }}
                          >
                            Track
                          </span>
                        ) : exp.category === "BNPL" ? (
                          exp.dueDate ? parseInt(exp.dueDate.includes("/") ? exp.dueDate.split("/")[1] : exp.dueDate.split("-")[2]) : null
                        ) : (
                          exp.dueDate && parseInt(exp.dueDate.split("-")[2])
                        )}
                      </span>
                      <span
                        className={`wave-badge ${
                          exp.category === "BNPL" && exp.wave
                            ? `wave-${exp.wave}`
                            : exp.category === "Wave" || exp.category === "Paycheck"
                              ? `wave-${exp.wave}`
                              : "other"
                        }`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        {exp.category === "BNPL"
                          ? (exp.wave ? <span style={{ fontWeight: 700 }}>Wave {exp.wave}</span> : null)
                          : exp.category === "Wave" || exp.category === "Paycheck"
                            ? getWaveLabel(exp.wave!, expenseListFrequency, t)
                            : t.otherCategory}
                        {exp.isRecurring && (
                          <span
                            title={
                              exp.recurrenceFrequency === "semi-annual"
                                ? t.recurringBadgeSemiAnnualFull ||
                                  "Semi-Annual"
                                : exp.recurrenceFrequency === "quarterly"
                                  ? t.recurringBadgeQuarterlyFull || "Quarterly"
                                  : exp.recurrenceFrequency === "annual"
                                    ? t.recurringBadgeAnnualFull || "Annual"
                                    : exp.recurrenceFrequency === "biweekly"
                                      ? t.recurringBadgeBiweeklyFull ||
                                        "Biweekly"
                                      : exp.recurrenceFrequency === "weekly"
                                        ? t.recurringBadgeWeeklyFull || "Weekly"
                                        : t.recurringBadgeMonthlyFull ||
                                          "Monthly"
                            }
                          >
                            <Repeat size={12} style={{ opacity: 0.9 }} />
                          </span>
                        )}
                      </span>
                      <div className="relative flex-shrink-0 z-[200]">
                        <button
                          className="p-0.5 rounded-lg text-[var(--text-secondary)] hover:text-[var(--text-color)] hover:bg-white/10 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(
                              openMenuId === exp._id ? null : exp._id,
                            );
                          }}
                        >
                          <MoreVertical size={18} />
                        </button>
                        {openMenuId === exp._id && (
                          <div className="expense-menu absolute right-0 top-[calc(100%+0.5rem)] bg-card-light dark:bg-card border border-card-border-light dark:border-card-border backdrop-blur-glass rounded-chip shadow-glass-light dark:shadow-glass z-[500] min-w-[150px] overflow-hidden">
                            {exp.paymentUrl && !exp.isPaid && (
                              <button
                                className="flex items-center gap-2 w-full px-3.5 py-3 border-none bg-transparent text-text-light dark:text-text text-[0.85rem] font-medium cursor-pointer transition-all hover:bg-black/[0.04] dark:hover:bg-white/[0.08]"
                                onClick={() => {
                                  payExpense(
                                    exp._id,
                                    exp.paymentUrl,
                                    exp.paymentType,
                                  );
                                  setOpenMenuId(null);
                                }}
                              >
                                <CreditCard size={16} /> {t.payHeader}
                              </button>
                            )}
                            <button
                              className="flex items-center gap-2 w-full px-3.5 py-3 border-none bg-transparent text-text-light dark:text-text text-[0.85rem] font-medium cursor-pointer transition-all hover:bg-black/[0.04] dark:hover:bg-white/[0.08] disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => {
                                editExpense(exp);
                                setOpenMenuId(null);
                              }}
                              disabled={exp.isPaid}
                            >
                              <Pencil size={16} /> {t.editExpense}
                            </button>
                            <button
                              className="flex items-center gap-2 w-full px-3.5 py-3 border-none bg-transparent text-red text-[0.85rem] font-medium cursor-pointer transition-all hover:bg-red/[0.08]"
                              onClick={() => {
                                deleteExpense(exp._id);
                                setOpenMenuId(null);
                              }}
                            >
                              <Trash2 size={16} /> {t.delete}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="hidden md:block overflow-x-auto rounded-chip">
              <table className="expenses-table w-full border-collapse">
                <thead>
                  <tr>
                    <th>{t.expenseNameHeader}</th>
                    <th>{t.amountHeader}</th>
                    <th>{t.dueDayHeader}</th>
                    <th>{t.categoryHeader}</th>
                    <th>{t.statusHeader}</th>
                    <th>{t.payHeader}</th>
                    <th>{t.actionsHeader}</th>
                  </tr>
                </thead>
                <tbody>
                  {expensesLoading ? (
                    // ✅ Show skeleton rows based on actual expense count
                    <>
                      {Array.from({
                        length: Math.max(sortedExpenses.length || 5, 1),
                      }).map((_, i) => (
                        <TableRowSkeleton key={i} />
                      ))}
                    </>
                  ) : sortedExpenses.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center p-8 text-muted-light dark:text-muted text-[0.9rem]"
                      >
                        <EmptyState
                          icon="📊"
                          title={t.dashboardPage.noExpensesToDisplay}
                          description={t.dashboardPage.expenseListWillAppear}
                        />
                      </td>
                    </tr>
                  ) : (
                    sortedExpenses.map((exp) => (
                      <tr key={exp._id} style={{ borderLeft: `4px solid ${exp.incomeSourceId ? (incomeSources.find((s: any) => s.id === exp.incomeSourceId)?.color ?? '#007AFF') : '#007AFF'}` }}>
                        <td>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                            {exp.name}
                            {exp.category === "BNPL" && (
                              <span style={{ background: "rgba(123,97,255,0.15)", color: "#7B61FF", borderRadius: "10px", padding: "1px 6px", fontSize: "0.65rem", fontWeight: 700, flexShrink: 0 }}>BNPL</span>
                            )}
                          </span>
                        </td>
                        <td>${exp.price.toFixed(2)}</td>
                        <td>
                          {exp.isTrackingOnly ? (
                            <span
                              className="track-only-badge"
                              style={{
                                fontSize: "0.75rem",
                                color: "var(--primary-blue)",
                                fontWeight: 600,
                              }}
                            >
                              Track Only
                            </span>
                          ) : exp.category === "BNPL" ? (
                            exp.dueDate ? parseInt(exp.dueDate.includes("/") ? exp.dueDate.split("/")[1] : exp.dueDate.split("-")[2]) : null
                          ) : (
                            exp.dueDate && parseInt(exp.dueDate.split("-")[2])
                          )}
                        </td>
                        <td>
                          <span
                            className={`wave-badge ${
                              exp.category === "BNPL" && exp.wave
                                ? `wave-${exp.wave}`
                                : exp.category === "Wave" || exp.category === "Paycheck"
                                  ? `wave-${exp.wave}`
                                  : "other"
                            }`}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            {(() => {
                              const srcName = exp.incomeSourceId
                                ? incomeSources.find((s: any) => s.id === exp.incomeSourceId)?.name
                                : null;
                              const srcTag = srcName ? (
                                <span style={{ opacity: 0.65, fontSize: "0.7rem", marginLeft: 3 }}>· {srcName}</span>
                              ) : null;
                              if (exp.category === "BNPL") return <>{exp.wave ? <span style={{ fontWeight: 700 }}>Wave {exp.wave}</span> : null}{srcTag}</>;
                              if (exp.category === "Wave" || exp.category === "Paycheck") return <>{getWaveLabel(exp.wave!, expenseListFrequency, t)}{srcTag}</>;
                              return <>{t.otherCategory}{srcTag}</>;
                            })()}
                            {exp.isRecurring && (
                              <span
                                title={
                                  exp.recurrenceFrequency === "semi-annual"
                                    ? t.recurringBadgeSemiAnnualFull ||
                                      "Semi-Annual"
                                    : exp.recurrenceFrequency === "quarterly"
                                      ? t.recurringBadgeQuarterlyFull ||
                                        "Quarterly"
                                      : exp.recurrenceFrequency === "annual"
                                        ? t.recurringBadgeAnnualFull || "Annual"
                                        : exp.recurrenceFrequency === "biweekly"
                                          ? t.recurringBadgeBiweeklyFull ||
                                            "Biweekly"
                                          : exp.recurrenceFrequency === "weekly"
                                            ? t.recurringBadgeWeeklyFull ||
                                              "Weekly"
                                            : t.recurringBadgeMonthlyFull ||
                                              "Monthly"
                                }
                              >
                                <Repeat size={14} style={{ opacity: 0.9 }} />
                              </span>
                            )}
                          </span>
                        </td>
                        <td>
                          <div className="inline-flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="w-4 h-4 cursor-pointer accent-primary"
                              checked={exp.isPaid}
                              onChange={() => togglePaid(exp._id, exp.isPaid)}
                            />
                            <span
                              className={`text-[0.85rem] font-medium ${exp.isPaid ? "text-green" : "text-muted-light dark:text-muted"}`}
                            >
                              {exp.isPaid ? t.paid : t.unpaid}
                            </span>
                          </div>
                        </td>
                        <td>
                          <button
                            title={
                              exp.paymentUrl
                                ? t.payVia(
                                    exp.paymentType === "app"
                                      ? "app"
                                      : exp.paymentType === "autopay"
                                        ? "autopay"
                                        : "web",
                                  )
                                : t.noPaymentUrl
                            }
                            onClick={() =>
                              payExpense(
                                exp._id,
                                exp.paymentUrl,
                                exp.paymentType,
                              )
                            }
                            disabled={exp.isPaid || !exp.paymentUrl}
                            className="p-1 rounded-lg text-green hover:bg-green/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {exp.paymentType === "autopay" ? (
                              <RefreshCw size={20} />
                            ) : (
                              <CreditCard
                                size={20}
                                style={{
                                  color: theme === "light" ? "#315EFF" : "#fff",
                                }}
                              />
                            )}
                          </button>
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              justifyContent: "center",
                            }}
                          >
                            <button
                              title={t.editExpense}
                              onClick={() => editExpense(exp)}
                              disabled={exp.isPaid}
                              className="p-1 rounded-lg text-primary hover:bg-primary/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Pencil size={20} />
                            </button>
                            <button
                              title={t.delete}
                              onClick={() => deleteExpense(exp._id)}
                              className="p-1 rounded-lg text-red hover:bg-red/10 transition-colors"
                            >
                              <Trash2 size={20} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Credit Card Accounts Section - Premium Only */}
          {userTier === "premium" &&
            plaidAccounts?.accounts &&
            plaidAccounts.accounts.some((bank) =>
              bank.accounts.some((acc) => acc.type === "credit"),
            ) && (
              <div className="w-full" style={{ marginTop: "1.5rem" }}>
                <h3
                  style={{
                    marginBottom: "1rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <CreditCard size={24} /> Credit Card Accounts
                </h3>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-3">
                  {plaidAccounts.accounts
                    .flatMap((bank) =>
                      bank.accounts
                        .filter((acc) => acc.type === "credit")
                        .map((acc) => ({
                          ...acc,
                          bankName: bank.bankName,
                        })),
                    )
                    .map((card: any) => (
                      <div
                        key={card.account_id}
                        className="rounded-card p-4"
                        style={{
                          background:
                            "linear-gradient(135deg, #FF6B6B 0%, #EE5A52 100%)",
                          backdropFilter: "blur(20px) saturate(180%)",
                        }}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <p className="text-xs font-medium text-white/70 m-0">
                            {card.bankName} - {card.name}
                          </p>
                        </div>
                        <h3 className="text-xl font-bold text-white m-0">
                          ${(card.balance || 0).toFixed(2)}
                        </h3>
                        <div className="h-px bg-white/20 my-2"></div>
                        <div className="flex flex-col gap-1">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-white/60">
                              Card ending in:
                            </span>
                            <span className="text-xs font-semibold text-white">
                              ****{card.mask}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-white/60">
                              Available:
                            </span>
                            <span className="text-xs font-semibold text-white">
                              ${(card.available || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

          {userTier !== "premium" ? (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-3 items-stretch">
              {expensesLoading || settingsLoading ? (
                // ✅ Show skeleton stat cards while loading
                <>
                  <StatCardSkeleton />
                  <StatCardSkeleton />
                  <StatCardSkeleton />
                </>
              ) : (
                <>
                  {Array.from({ length: waveCount }, (_, i) => {
                    // const waveNum = i + 1;
                    const currentWaveAmount =
                      i === 0
                        ? wave1Amount
                        : i === 1
                          ? wave2Amount
                          : i === 2
                            ? wave3Amount
                            : wave4Amount;

                    return (
                      <div
                        key={i + 1}
                        className="bg-card-light dark:bg-card border border-card-border-light dark:border-card-border backdrop-blur-glass rounded-card p-4 shadow-glass-light dark:shadow-glass"
                      >
                        <div className="flex justify-between items-center mb-2">
                          <p className="text-xs font-medium text-muted-light dark:text-muted m-0">
                            {getWaveLabel(i + 1, expenseListFrequency, t)}
                          </p>
                        </div>

                        {/* Editable Wave Amount */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            justifyContent: "center",
                          }}
                        >
                          <h3
                            className="text-xl font-bold text-text-light dark:text-text"
                            style={{ margin: 0 }}
                          >
                            ${currentWaveAmount.toFixed(2)}
                          </h3>
                          <button
                            title={t.dashboardPage.editWaveAmount}
                            onClick={() => {
                              const newAmount = prompt(
                                `${t.dashboardPage.enterNewAmountFor} ${getWaveLabel(i + 1, expenseListFrequency, t)}:`,
                                currentWaveAmount.toString(),
                              );
                              if (newAmount !== null) {
                                const parsedAmount = parseFloat(newAmount);
                                if (!isNaN(parsedAmount) && parsedAmount >= 0) {
                                  if (i === 0) setWave1Amount(parsedAmount);
                                  else if (i === 1)
                                    setWave2Amount(parsedAmount);
                                  else if (i === 2)
                                    setWave3Amount(parsedAmount);
                                  else setWave4Amount(parsedAmount);
                                  toast.success(
                                    t.dashboardPage.waveAmountUpdated,
                                  );
                                } else {
                                  toast.error(t.dashboardPage.invalidAmount);
                                }
                              }
                            }}
                            className="p-1 rounded-lg text-muted-light dark:text-muted hover:text-text-light dark:hover:text-text hover:bg-black/[0.05] dark:hover:bg-white/10 transition-colors"
                          >
                            <Pencil size={16} />
                          </button>
                        </div>

                        <div className="h-px bg-card-border-light dark:bg-card-border my-2"></div>
                        <div className="flex flex-col gap-1">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-light dark:text-muted">
                              {t.dashboardPage.expenses}:
                            </span>
                            <span className="text-xs font-semibold text-text-light dark:text-text">
                              ${waveTotals[i].toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-light dark:text-muted">
                              {t.dashboardPage.count}:
                            </span>
                            <span className="text-xs font-semibold text-text-light dark:text-text">
                              {waveCounts[i]} {t.items}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-light dark:text-muted">
                              {t.dashboardPage.balance}:
                            </span>
                            <span
                              className={`text-xs font-semibold ${waveBalances[i] >= 0 ? "text-green" : "text-red"}`}
                            >
                              ${waveBalances[i].toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Total Unpaid Wave Expenses Card */}
                  <div className="bg-card-light dark:bg-card border border-card-border-light dark:border-card-border backdrop-blur-glass rounded-card p-4 shadow-glass-light dark:shadow-glass">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-xs font-medium text-muted-light dark:text-muted m-0">
                        {t.totalUnpaidWaveExpenses}
                      </p>
                    </div>
                    <h3 className="text-xl font-bold text-text-light dark:text-text">
                      ${totalWaveAmount.toFixed(2)}
                    </h3>
                    <div className="h-px bg-card-border-light dark:bg-card-border my-2"></div>
                    <div className="flex flex-col gap-1">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-light dark:text-muted">
                          {t.dashboardPage.totalBudget}:
                        </span>
                        <span className="text-xs font-semibold text-text-light dark:text-text">
                          $
                          {(expenseListFrequency === "weekly"
                            ? wave1Amount +
                              wave2Amount +
                              wave3Amount +
                              wave4Amount
                            : expenseListFrequency === "biweekly"
                              ? wave1Amount + wave2Amount
                              : wave1Amount
                          ).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-light dark:text-muted">
                          {t.wave(1)}:
                        </span>
                        <span className="text-xs font-semibold text-text-light dark:text-text">
                          $
                          {expenses
                            .filter(
                              (e) =>
                                e.wave === 1 &&
                                (e.category === "Wave" ||
                                  e.category === "Paycheck"),
                            )
                            .reduce((sum, e) => sum + e.price, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                      {expenseListFrequency === "biweekly" && (
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-muted-light dark:text-muted">
                            {t.wave(2)}:
                          </span>
                          <span className="text-xs font-semibold text-text-light dark:text-text">
                            $
                            {expenses
                              .filter(
                                (e) =>
                                  e.wave === 2 &&
                                  (e.category === "Wave" ||
                                    e.category === "Paycheck"),
                              )
                              .reduce((sum, e) => sum + e.price, 0)
                              .toFixed(2)}
                          </span>
                        </div>
                      )}
                      {expenseListFrequency === "weekly" && (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-light dark:text-muted">
                              {t.week(2)}:
                            </span>
                            <span className="text-xs font-semibold text-text-light dark:text-text">
                              $
                              {expenses
                                .filter(
                                  (e) =>
                                    e.wave === 2 &&
                                    (e.category === "Wave" ||
                                      e.category === "Paycheck"),
                                )
                                .reduce((sum, e) => sum + e.price, 0)
                                .toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-light dark:text-muted">
                              {t.week(3)}:
                            </span>
                            <span className="text-xs font-semibold text-text-light dark:text-text">
                              $
                              {expenses
                                .filter(
                                  (e) =>
                                    e.wave === 3 &&
                                    (e.category === "Wave" ||
                                      e.category === "Paycheck"),
                                )
                                .reduce((sum, e) => sum + e.price, 0)
                                .toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-light dark:text-muted">
                              {t.week(4)}:
                            </span>
                            <span className="text-xs font-semibold text-text-light dark:text-text">
                              $
                              {expenses
                                .filter(
                                  (e) =>
                                    e.wave === 4 &&
                                    (e.category === "Wave" ||
                                      e.category === "Paycheck"),
                                )
                                .reduce((sum, e) => sum + e.price, 0)
                                .toFixed(2)}
                            </span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-light dark:text-muted">
                          Total {t.items}:
                        </span>
                        <span className="text-xs font-semibold text-text-light dark:text-text">
                          {totalWaveExpenses}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Other Unpaid Expenses Card */}
                  <div className="bg-card-light dark:bg-card border border-card-border-light dark:border-card-border backdrop-blur-glass rounded-card p-4 shadow-glass-light dark:shadow-glass">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-xs font-medium text-muted-light dark:text-muted m-0">
                        {t.otherUnpaidExpenses}
                      </p>
                    </div>
                    <h3 className="text-xl font-bold text-text-light dark:text-text">
                      ${otherExpensesTotal.toFixed(2)}
                    </h3>
                    <div className="h-px bg-card-border-light dark:bg-card-border my-2"></div>
                    <div className="flex flex-col gap-1">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-light dark:text-muted">
                          Total {t.items}:
                        </span>
                        <span className="text-xs font-semibold text-text-light dark:text-text">
                          {
                            expenses.filter(
                              (e) => e.category === "Other" && !e.isPaid,
                            ).length
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3 items-stretch">
              {(() => {
                const hasSingleIncome =
                  incomeSources.length === 0 ||
                  (incomeSources.length === 1 && !incomeSources[0].id);
                const mainWaveCount =
                  expenseListFrequency === "weekly"
                    ? 4
                    : expenseListFrequency === "biweekly"
                      ? 2
                      : 1;
                // ✅ FIX: Consistently filter main income expenses by incomeSourceId
                // Show ALL expenses (paid and unpaid) with no incomeSourceId (main income)
                // This ensures wave cards show the same numbers whether viewing alone or with other sources
                const mainExpenses = expenses.filter(
                  (e) =>
                    (!e.incomeSourceId ||
                      e.incomeSourceId === "" ||
                      e.incomeSourceId === "main") &&
                    (e.category === "Wave" || e.category === "Paycheck" || e.category === "BNPL"),
                );
                const totalWaveExpenses = mainExpenses.reduce(
                  (sum, e) => sum + e.price,
                  0,
                );
                // const totalWaveBalance =
                //   waveAmount * mainWaveCount - totalWaveExpenses;
                const otherExpenses = expenses.filter(
                  (e) => e.category === "Other",
                );
                const totalOtherExpenses = otherExpenses.reduce(
                  (sum, e) => sum + e.price,
                  0,
                );
                const totalIncome =
                  wave1Amount +
                  wave2Amount +
                  wave3Amount +
                  wave4Amount +
                  incomeSources.reduce(
                    (sum, inc) =>
                      sum +
                      (inc.amount1 ?? 0) +
                      (inc.amount2 ?? 0) +
                      (inc.amount3 ?? 0) +
                      (inc.amount4 ?? 0),
                    0,
                  );
                if (hasSingleIncome) {
                  // Single income source - use waveAmount with wave cards
                  return (
                    <>
                      {Array.from({ length: mainWaveCount }, (_, i) => {
                        const waveNum = i + 1;
                        const currentWaveAmount =
                          i === 0
                            ? wave1Amount
                            : i === 1
                              ? wave2Amount
                              : i === 2
                                ? wave3Amount
                                : wave4Amount; // Get correct amount per wave

                        const isLastWave = waveNum === mainWaveCount;
                        const waveExpenses = mainExpenses.filter(
                          (e) =>
                            e.wave === waveNum ||
                            (isLastWave && e.category === "BNPL" && !e.wave),
                        );
                        const waveTotal = waveExpenses.reduce(
                          (sum, e) => sum + e.price,
                          0,
                        );
                        const waveBalance = currentWaveAmount - waveTotal; // Use currentWaveAmount
                        const waveCountItems = waveExpenses.length;

                        return (
                          <div
                            key={i}
                            className="bg-card-light dark:bg-card border border-card-border-light dark:border-card-border backdrop-blur-glass rounded-card p-4 shadow-glass-light dark:shadow-glass"
                          >
                            <div className="flex justify-between items-center mb-2">
                              <p className="text-xs font-medium text-muted-light dark:text-muted m-0">
                                {getWaveLabel(waveNum, expenseListFrequency, t)}
                              </p>
                            </div>

                            {/* Editable Wave Amount */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                                justifyContent: "center",
                              }}
                            >
                              <h3
                                className="text-xl font-bold text-text-light dark:text-text"
                                style={{ margin: 0 }}
                              >
                                ${currentWaveAmount.toFixed(2)}
                              </h3>
                              <button
                                title={t.dashboardPage.editWaveAmount}
                                onClick={() => {
                                  const newAmount = prompt(
                                    `${t.dashboardPage.enterNewAmountFor} ${getWaveLabel(waveNum, expenseListFrequency, t)}:`,
                                    currentWaveAmount.toString(),
                                  );
                                  if (newAmount !== null) {
                                    const parsedAmount = parseFloat(newAmount);
                                    if (
                                      !isNaN(parsedAmount) &&
                                      parsedAmount >= 0
                                    ) {
                                      if (i === 0) setWave1Amount(parsedAmount);
                                      else if (i === 1)
                                        setWave2Amount(parsedAmount);
                                      else if (i === 2)
                                        setWave3Amount(parsedAmount);
                                      else setWave4Amount(parsedAmount);
                                      toast.success(
                                        t.dashboardPage.waveAmountUpdated,
                                      );
                                    } else {
                                      toast.error(
                                        t.dashboardPage.invalidAmount,
                                      );
                                    }
                                  }
                                }}
                                className="p-1 rounded-lg text-muted-light dark:text-muted hover:text-text-light dark:hover:text-text hover:bg-black/[0.05] dark:hover:bg-white/10 transition-colors"
                              >
                                <Pencil size={16} />
                              </button>
                            </div>

                            <div className="h-px bg-card-border-light dark:bg-card-border my-2"></div>
                            <div className="flex flex-col gap-1">
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-muted-light dark:text-muted">
                                  {t.dashboardPage.expenses}:
                                </span>
                                <span className="text-xs font-semibold text-text-light dark:text-text">
                                  ${waveTotal.toFixed(2)}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-muted-light dark:text-muted">
                                  {t.dashboardPage.count}:
                                </span>
                                <span className="text-xs font-semibold text-text-light dark:text-text">
                                  {waveCountItems} {t.items}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-muted-light dark:text-muted">
                                  {t.dashboardPage.balance}:
                                </span>
                                <span
                                  className={`text-xs font-semibold ${waveBalance >= 0 ? "text-green" : "text-red"}`}
                                >
                                  ${waveBalance.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* Total Unpaid Wave Expenses Card */}
                      <div className="bg-card-light dark:bg-card border border-card-border-light dark:border-card-border backdrop-blur-glass rounded-card p-4 shadow-glass-light dark:shadow-glass">
                        <div className="flex justify-between items-center mb-2">
                          <p className="text-xs font-medium text-muted-light dark:text-muted m-0">
                            {t.totalUnpaidWaveExpenses}
                          </p>
                        </div>
                        <h3 className="text-xl font-bold text-text-light dark:text-text">
                          ${totalWaveExpenses.toFixed(2)}
                        </h3>
                        <div className="h-px bg-card-border-light dark:bg-card-border my-2"></div>
                        <div className="flex flex-col gap-1">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-light dark:text-muted">
                              {t.dashboardPage.totalBudget}:
                            </span>
                            <span className="text-xs font-semibold text-text-light dark:text-text">
                              $
                              {(expenseListFrequency === "weekly"
                                ? wave1Amount +
                                  wave2Amount +
                                  wave3Amount +
                                  wave4Amount
                                : expenseListFrequency === "biweekly"
                                  ? wave1Amount + wave2Amount
                                  : wave1Amount
                              ).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-light dark:text-muted">
                              {t.wave(1)}:
                            </span>
                            <span className="text-xs font-semibold text-text-light dark:text-text">
                              $
                              {expenses
                                .filter(
                                  (e) =>
                                    e.wave === 1 &&
                                    (e.category === "Wave" ||
                                      e.category === "Paycheck"),
                                )
                                .reduce((sum, e) => sum + e.price, 0)
                                .toFixed(2)}
                            </span>
                          </div>
                          {expenseListFrequency === "biweekly" && (
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-muted-light dark:text-muted">
                                {t.wave(2)}:
                              </span>
                              <span className="text-xs font-semibold text-text-light dark:text-text">
                                $
                                {expenses
                                  .filter(
                                    (e) =>
                                      e.wave === 2 &&
                                      (e.category === "Wave" ||
                                        e.category === "Paycheck"),
                                  )
                                  .reduce((sum, e) => sum + e.price, 0)
                                  .toFixed(2)}
                              </span>
                            </div>
                          )}
                          {expenseListFrequency === "weekly" && (
                            <>
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-muted-light dark:text-muted">
                                  {t.week(2)}:
                                </span>
                                <span className="text-xs font-semibold text-text-light dark:text-text">
                                  $
                                  {expenses
                                    .filter(
                                      (e) =>
                                        e.wave === 2 &&
                                        (e.category === "Wave" ||
                                          e.category === "Paycheck"),
                                    )
                                    .reduce((sum, e) => sum + e.price, 0)
                                    .toFixed(2)}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-muted-light dark:text-muted">
                                  {t.week(3)}:
                                </span>
                                <span className="text-xs font-semibold text-text-light dark:text-text">
                                  $
                                  {expenses
                                    .filter(
                                      (e) =>
                                        e.wave === 3 &&
                                        (e.category === "Wave" ||
                                          e.category === "Paycheck"),
                                    )
                                    .reduce((sum, e) => sum + e.price, 0)
                                    .toFixed(2)}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-muted-light dark:text-muted">
                                  {t.week(4)}:
                                </span>
                                <span className="text-xs font-semibold text-text-light dark:text-text">
                                  $
                                  {expenses
                                    .filter(
                                      (e) =>
                                        e.wave === 4 &&
                                        (e.category === "Wave" ||
                                          e.category === "Paycheck"),
                                    )
                                    .reduce((sum, e) => sum + e.price, 0)
                                    .toFixed(2)}
                                </span>
                              </div>
                            </>
                          )}
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-light dark:text-muted">
                              Total {t.items}:
                            </span>
                            <span className="text-xs font-semibold text-text-light dark:text-text">
                              {mainExpenses.length}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Other Unpaid Expenses Card */}
                      <div className="bg-card-light dark:bg-card border border-card-border-light dark:border-card-border backdrop-blur-glass rounded-card p-4 shadow-glass-light dark:shadow-glass">
                        <div className="flex justify-between items-center mb-2">
                          <p className="text-xs font-medium text-muted-light dark:text-muted m-0">
                            {t.otherUnpaidExpenses}
                          </p>
                        </div>
                        <h3 className="text-xl font-bold text-text-light dark:text-text">
                          ${totalOtherExpenses.toFixed(2)}
                        </h3>
                        <div className="h-px bg-card-border-light dark:bg-card-border my-2"></div>
                        <div className="flex flex-col gap-1">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-light dark:text-muted">
                              Total {t.items}:
                            </span>
                            <span className="text-xs font-semibold text-text-light dark:text-text">
                              {otherExpenses.length}
                            </span>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                } else {
                  // Multiple income sources - 4 summary cards
                  const allSources = [
                    {
                      id: "main",
                      name: "Main Income",
                      amount1: wave1Amount,
                      amount2: wave2Amount,
                      amount3: wave3Amount,
                      amount4: wave4Amount,
                      frequency: expenseListFrequency,
                      color: "#007AFF",
                    },
                    ...incomeSources,
                  ];

                  return (
                    <>
                      {allSources.map((income) => {
                        const sourceWaveCount =
                          income.frequency === "weekly"
                            ? 4
                            : income.frequency === "biweekly"
                              ? 2
                              : 1;

                        const sourceExpenses = expenses.filter(
                          (e) =>
                            (income.id === "main"
                              ? !e.incomeSourceId || e.incomeSourceId === "" || e.incomeSourceId === "main"
                              : e.incomeSourceId === income.id) &&
                            (e.category === "Wave" ||
                              e.category === "Paycheck" ||
                              e.category === "BNPL"),
                        );

                        const totalExpenses = sourceExpenses.reduce(
                          (sum, e) => sum + e.price,
                          0,
                        );

                        // Sum all wave amounts - check if main income or additional source
                        const totalIncome =
                          income.id === "main"
                            ? wave1Amount +
                              wave2Amount +
                              wave3Amount +
                              wave4Amount
                            : (income.amount1 ?? 0) +
                              (income.amount2 ?? 0) +
                              (income.amount3 ?? 0) +
                              (income.amount4 ?? 0);

                        const totalBalance = totalIncome - totalExpenses;

                        const waveDetails = Array.from(
                          { length: sourceWaveCount },
                          (_, i) => {
                            const waveNum = i + 1;
                            const isLastWave = waveNum === sourceWaveCount;
                            const waveExpenses = sourceExpenses.filter(
                              (e) =>
                                e.wave === waveNum ||
                                // BNPL with no wave saved: assign to last wave of source
                                (isLastWave && e.category === "BNPL" && !e.wave),
                            );
                            const waveTotal = waveExpenses.reduce(
                              (sum, e) => sum + e.price,
                              0,
                            );

                            // Get the correct amount for this specific wave
                            const waveAmount =
                              income.id === "main"
                                ? waveNum === 1
                                  ? wave1Amount
                                  : waveNum === 2
                                    ? wave2Amount
                                    : waveNum === 3
                                      ? wave3Amount
                                      : waveNum === 4
                                        ? wave4Amount
                                        : 0
                                : waveNum === 1
                                  ? (income.amount1 ?? 0)
                                  : waveNum === 2
                                    ? (income.amount2 ?? 0)
                                    : waveNum === 3
                                      ? (income.amount3 ?? 0)
                                      : waveNum === 4
                                        ? (income.amount4 ?? 0)
                                        : 0;

                            const waveBalance = waveAmount - waveTotal;

                            return {
                              waveNum,
                              waveTotal,
                              waveBalance,
                              waveCountItems: waveExpenses.length,
                            };
                          },
                        );

                        return (
                          <div
                            key={income.id}
                            className="relative overflow-hidden bg-card-light dark:bg-card border border-card-border-light dark:border-card-border backdrop-blur-glass rounded-card p-4 shadow-glass-light dark:shadow-glass"
                          >
                            {/* Colored top accent */}
                            <div
                              className="absolute top-0 left-0 right-0 h-[3px] rounded-t-card"
                              style={{ background: income.color }}
                            />
                            <div className="flex justify-between items-center mb-2 mt-1">
                              <p className="text-xs font-medium text-muted-light dark:text-muted m-0">
                                {income.name} ({sourceWaveCount}{" "}
                                {sourceWaveCount === 1 ? "wave" : "waves"})
                              </p>
                              <span
                                className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                                style={{
                                  background: `${income.color}22`,
                                  color: income.color,
                                }}
                              >
                                ${totalIncome.toFixed(0)}
                              </span>
                            </div>
                            <h3
                              className="text-xl font-bold text-text-light dark:text-text"
                              style={{ margin: 0 }}
                            >
                              ${totalBalance.toFixed(2)}
                            </h3>
                            <p className="text-[10px] text-muted-light dark:text-muted m-0 mt-0.5">
                              {t.dashboardPage.balance}
                            </p>
                            <div className="h-px bg-card-border-light dark:bg-card-border my-2"></div>
                            <div className="flex flex-col gap-0.5">
                              {waveDetails.map((wd, wdIdx) => (
                                <div
                                  key={wd.waveNum}
                                  className={wdIdx > 0 ? "pt-0.5" : ""}
                                >
                                  <div className="flex justify-between items-center">
                                    <span className="text-[11px] font-medium text-muted-light dark:text-muted">
                                      W{wd.waveNum} spent
                                    </span>
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-[11px] font-semibold text-text-light dark:text-text">
                                        ${wd.waveTotal.toFixed(2)}
                                      </span>
                                      <span
                                        className={`text-[10px] font-semibold ${wd.waveBalance >= 0 ? "text-green" : "text-red"}`}
                                      >
                                        ({wd.waveBalance >= 0 ? "+" : ""}
                                        {wd.waveBalance.toFixed(0)})
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                              <div className="flex justify-between items-center mt-1.5 pt-1.5 border-t border-card-border-light dark:border-card-border">
                                <span className="text-[11px] font-medium text-muted-light dark:text-muted">
                                  Net
                                </span>
                                <span
                                  className={`text-sm font-bold ${totalBalance >= 0 ? "text-green" : "text-red"}`}
                                >
                                  {totalBalance >= 0 ? "+" : ""}
                                  {totalBalance.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div className="bg-card-light dark:bg-card border border-card-border-light dark:border-card-border backdrop-blur-glass rounded-card p-4 shadow-glass-light dark:shadow-glass">
                        <div className="flex justify-between items-center mb-2">
                          <p className="text-xs font-medium text-muted-light dark:text-muted m-0">
                            {t.totalUnpaidWaveExpenses}
                          </p>
                        </div>
                        <h3 className="text-xl font-bold text-text-light dark:text-text">
                          ${totalWaveExpenses.toFixed(2)}
                        </h3>
                        <div className="h-px bg-card-border-light dark:bg-card-border my-2"></div>
                        <div className="flex flex-col gap-1">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-light dark:text-muted">
                              Total Income:
                            </span>
                            <span className="text-xs font-semibold text-text-light dark:text-text">
                              ${totalIncome.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-light dark:text-muted">
                              Total {t.items}:
                            </span>
                            <span className="text-xs font-semibold text-text-light dark:text-text">
                              {waveCounts.reduce((a, b) => a + b, 0) +
                                (mainExpenses.some(
                                  (e) =>
                                    e.incomeSourceId &&
                                    incomeSources.some(
                                      (s) => s.id === e.incomeSourceId,
                                    ),
                                )
                                  ? 1
                                  : 0)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-card-light dark:bg-card border border-card-border-light dark:border-card-border backdrop-blur-glass rounded-card p-4 shadow-glass-light dark:shadow-glass">
                        <div className="flex justify-between items-center mb-2">
                          <p className="text-xs font-medium text-muted-light dark:text-muted m-0">
                            {t.otherUnpaidExpenses}
                          </p>
                        </div>
                        <h3 className="text-xl font-bold text-text-light dark:text-text">
                          ${totalOtherExpenses.toFixed(2)}
                        </h3>
                        <div className="h-px bg-card-border-light dark:bg-card-border my-2"></div>
                        <div className="flex flex-col gap-1">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-light dark:text-muted">
                              Total {t.items}:
                            </span>
                            <span className="text-xs font-semibold text-text-light dark:text-text">
                              {otherExpenses.length}
                            </span>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                }
              })()}
            </div>
          )}
        </div>
        {/* Email Dialog */}
        <EmailDialog
          emailDialogOpen={emailDialogOpen}
          recipientEmail={recipientEmail}
          emailError={emailError}
          emailFormat={emailFormat}
          language={language}
          t={t}
          loading={loading}
          onClose={() => {
            setEmailDialogOpen(false);
            setRecipientEmail("");
            setEmailError("");
          }}
          onEmailChange={setRecipientEmail}
          onFormatChange={setEmailFormat}
          onSend={sendEmailReport}
          setEmailError={setEmailError}
        />
        {/* Export Dialog */}
        <ExportDialog
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
        />
        {/* AI Advisor Modal */}
        <AiModal open={aiModalOpen} onClose={() => setAiModalOpen(false)} />
        {/* Onboarding Modal */}
        <OnboardingSteps
          open={showOnboarding}
          wave1Amount={wave1Amount}
          setWave1Amount={setWave1Amount}
          wave2Amount={wave2Amount}
          setWave2Amount={setWave2Amount}
          wave3Amount={wave3Amount}
          setWave3Amount={setWave3Amount}
          wave4Amount={wave4Amount}
          setWave4Amount={setWave4Amount}
          setIsSetupComplete={setIsSetupComplete}
          onboardingStep={onboardingStep}
          setOnboardingStep={setOnboardingStep}
          language={language}
          t={t}
          toast={toast}
          token={token}
          API_URL={API_URL}
          onClose={() => setShowOnboarding(false)}
        />
      </div>
      <AppModal
        open={confirmModal.open}
        onClose={() => setConfirmModal((prev) => ({ ...prev, open: false }))}
        title={confirmModal.title}
        variant="confirm"
        actions={{
          primary: {
            label: t.dashboardPage.confirm,
            onClick: confirmModal.onConfirm,
          },
          secondary: {
            label: t.dashboardPage.cancel,
            onClick: () =>
              setConfirmModal((prev) => ({ ...prev, open: false })),
          },
        }}
      >
        <p className="text-[var(--text-color)]">{confirmModal.message}</p>
      </AppModal>

      {/* Export Menu - Hidden but accessible via ref */}
      <div
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          pointerEvents: "none",
        }}
      >
        <ExportMenu
          ref={exportMenuRef}
          expenses={expenses}
          waveAmounts={[wave1Amount, wave2Amount, wave3Amount, wave4Amount]}
          incomeSources={incomeSources}
          expenseListFrequency={expenseListFrequency}
          waveCount={waveCount}
          waveTotals={waveTotals}
          waveCounts={waveCounts}
          waveBalances={waveBalances}
          t={t}
          language={language}
          toast={toast}
          getWaveLabel={getWaveLabel}
        />
      </div>

      {/* Plaid Link Modal */}
      {linkBankOpen && (
        <PlaidLinkButton
          token={token}
          onSuccess={() => {
            toast.success(t.dashboardPage.bankLinkedSuccess);
            ReactGA.event({
              category: "Plaid",
              action: "Bank Linked",
              label: "Success",
            });
            setLinkBankOpen(false);
            refetchExpenses();
          }}
          onExit={() => setLinkBankOpen(false)}
          language={language}
          userTier={userTier}
          hideTooltip={true}
        />
      )}

      {/* Quick Add Modal */}
      <QuickAddModal
        open={quickAddOpen}
        onClose={() => setQuickAddOpen(false)}
      />

      {/* Selective Reset Modal - for deleting one-time expenses */}
      <SelectiveResetModal
        open={selectiveResetOpen}
        onClose={() => setSelectiveResetOpen(false)}
        expenses={oneTimeExpensesToDelete}
        onConfirm={handleDeleteOneTimeExpenses}
        t={t}
      />
    </>
  );
}
export default ExpensesList;
